import React, { useEffect } from "react";
import { createContext, useState } from "react";
import { useLocation } from "@reach/router";
import useLocalStorage from "../hooks/useLocalStorage";
import { LANGUAGE_ARABIC, LANGUAGE_ENGLISH } from "./languages";
import translationsEn from "../../locales/en/translations.json";
import translationsAr from "../../locales/ar/translations.json";
import { useI18next } from "gatsby-plugin-react-i18next";

const getLanguageFromPathname = (pathname = "") =>
  pathname.startsWith("/en") ? LANGUAGE_ENGLISH : LANGUAGE_ARABIC;

const getLanguageFromI18next = (lang = "") =>
  lang === LANGUAGE_ENGLISH ? LANGUAGE_ENGLISH : LANGUAGE_ARABIC;

const getTranslationsByLanguage = (lang) =>
  lang === LANGUAGE_ARABIC ? translationsAr : translationsEn;

const initialState = {
  language: LANGUAGE_ARABIC,
  updateLanguage: () => {},
  translations: translationsAr,
};

const StateContext = createContext(initialState);

export function StateProvider({ children }) {
  const { language: routedLanguage, i18n } = useI18next();
  const location = useLocation();
  const initialLanguage =
    location?.pathname
      ? getLanguageFromPathname(location.pathname)
      : getLanguageFromI18next(routedLanguage || i18n.language);
  const [language, setLanguage] = useState(initialLanguage);
  const [translations, setTranslations] = useState(
    getTranslationsByLanguage(initialLanguage)
  );
  const [storedLanguage, setLanguageInStorage] = useLocalStorage(
    "language",
    initialLanguage
  );

  useEffect(() => {
    const routeLanguage = location?.pathname
      ? getLanguageFromPathname(location.pathname)
      : getLanguageFromI18next(routedLanguage || i18n.language);
    const nextLanguage = routeLanguage || initialLanguage;

    if (routeLanguage !== storedLanguage) {
      setLanguageInStorage(routeLanguage);
    }

    setLanguage(nextLanguage);
    setTranslations(getTranslationsByLanguage(nextLanguage));
  }, [initialLanguage, location?.pathname, routedLanguage, i18n.language, storedLanguage]);

  /** 
    @desc updates the language in app state and in local storage
    @param {string} lang - new language value for updating (e.g updateLanguage('en') or updateLanguage('ar'))
  */
  const updateLanguage = (lang) => {
    setLanguageInStorage(lang);
    setLanguage(lang);
    setTranslations(getTranslationsByLanguage(lang));
  };

  return (
    <StateContext.Provider value={{ language, updateLanguage, translations }}>
      {children}
    </StateContext.Provider>
  );
}

export default StateContext;
